import React, { useState } from 'react';
import { MaterialReactTable } from 'material-react-table';
import { Tabs, Tab, Typography, Box, CircularProgress, useTheme } from "@mui/material";
import { useGetAllClaimsQuery } from "store/slices/apis/claimsPackageSlice";
import { useGetAllChangeQuery } from "store/slices/apis/changeTrackerPackageSlice";
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import {ReactComponent as PhoneIcon} from "assets/PwC_Funct_Icons_Call_Fill_Black_RGB.svg"

const milestoneRows = [
    {
        name: 'Basic Engneering Completion',
        originalPlanDate: '28 Jan 2025',
        actualDate: '31 Jan 2025',
        delayInDays: '3 days',
        reasonForDelay: 'Heavier rainfall than expected',
        projectManagerName: 'Mr. A Kumar',
        projectManagerPhone: '9999999999',
    }
];

const risksRows = [
    {
        description: 'Delay in civil foundation work due to monsoon rains',
        impact: 'High',
        likelihood: 'High',
        status: 'Open',
        mitigationPlan: 'Improve site drainage; deploy prefabricated elements if needed.',
        ownerName: 'Rahul Mehta',
        ownerPhone: '9876543210'
    },
    {
        description: 'Delay in structural design approvals',
        impact: 'Moderate',
        likelihood: 'High',
        status: 'Open',
        mitigationPlan: 'Early design freeze and weekly design coordination meetings.',
        ownerName: 'Anita Sharma',
        ownerPhone: '9123456789'
    },
    {
        description: 'Long lead equipment may not arrive on schedule',
        impact: 'Extreme',
        likelihood: 'Extreme',
        status: 'Open',
        mitigationPlan: 'Identify alternate vendors and expedite shipping.',
        ownerName: 'Vikram Desai',
        ownerPhone: '9988776655'
    },
    {
        description: 'Cranes and heavy equipment stuck due to poor access',
        impact: 'Moderate',
        likelihood: 'Moderate',
        status: 'Close',
        mitigationPlan: 'Strengthen access roads and schedule light-duty pre-works.',
        ownerName: 'Neha Gupta',
        ownerPhone: '9811122233'
    },
];

const columnsMilestones = [
    { accessorKey: 'name', header: 'Name', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' } },
    { accessorKey: 'originalPlanDate', header: 'Original Plan Date', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' } },
    { accessorKey: 'actualDate', header: 'Actual Date', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' } },
    { accessorKey: 'delayInDays', header: 'Delay in Days', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' } },
    { accessorKey: 'reasonForDelay', header: 'Reason for Delay', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center', sx: { whiteSpace: 'nowrap' } } },
    {
        header: 'Responsible Project Manager',
        id: 'projectManager',
        muiTableHeadCellProps: { align: 'center' },
        muiTableBodyCellProps: {
            align: 'center',
            sx: {
                padding: '10px 14px',
                fontSize: '14px',
                lineHeight: '1.5',
                whiteSpace: 'normal'
            }
        },
        Cell: ({ row }) => {
            const name = row.original.projectManagerName;
            const phone = row.original.projectManagerPhone;

            return (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                    <Box component="span" sx={{ whiteSpace: 'nowrap', lineHeight: 1 }}>{name}</Box>
                    <Box component="a" href={`tel:${phone}`} sx={{ color: '#1976d2', textDecoration: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' }} >
                        {/* <LocalPhoneIcon fontSize="small" /> */}
                        <PhoneIcon style={{width:"20px", height:"20px", fill:"#1976d2"}}/>
                    </Box>
                </Box>
            );
        }
    }

];


const columnsRisks = [
    { accessorKey: 'description', header: 'Risk Description', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' } },
    { accessorKey: 'impact', header: 'Impact', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' } },
    { accessorKey: 'likelihood', header: 'Likelihood', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' } },
    { accessorKey: 'status', header: 'Current Status', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' } },
    { accessorKey: 'mitigationPlan', header: 'Mitigation Plan Summary', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center', sx: { whiteSpace: 'nowrap' } } },
    {
        header: 'Owner',
        id: 'owner',
        muiTableHeadCellProps: { align: 'center' },
        muiTableBodyCellProps: {
            align: 'center',
            sx: {
                padding: '10px 14px',
                fontSize: '14px',
                lineHeight: '1.5',
                whiteSpace: 'normal',
            }
        },
        Cell: ({ row }) => {
            const name = row.original.ownerName;
            const phone = row.original.ownerPhone;

            return (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                    <Box component="span" sx={{ whiteSpace: 'nowrap', lineHeight: 1 }}>{name}</Box>
                    <Box component="a" href={`tel:${phone}`} sx={{ color: '#1976d2', textDecoration: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' }} >
                        {/* <LocalPhoneIcon fontSize="small" /> */}
                        <PhoneIcon style={{width:"20px", height:"20px" , fill:"#1976d2" }}/>
                    </Box>
                </Box>
            );
        }
    }
];


// Format helper
const formatDate = (dateString) =>
    dateString ? dayjs(dateString).format('DD/MM/YY') : '';

// // Flatten formatter (no children)
// const formatScheduleData = (data) =>
//     data.map((item) => ({
//         claim_number: item.claim_number,
//         package: item.package,
//         sub_package: item.sub_package,
//         approved_status: item.approved_status,
//         status: item.status,
//         createddate: formatDate(item.createddate),
//         contract_number: item.contract_number,
//         consultant_contractor: item.consultant_contractor,
//         claim_raised_to: item.claim_raised_to,
//         claim_received_on: formatDate(item.claim_received_on),
//         claim_raised_by_refrence: item.claim_raised_by_refrence,
//         claim_raised_by_date_received: formatDate(item.claim_raised_by_date_received),
//         claim_raised_to_reference: item.claim_raised_to_reference,
//         claim_raised_to_date_of_response: formatDate(item.claim_raised_to_date_of_response),
//         claim_raised_to_position: item.claim_raised_to_position,
//         claim_raised_by_estimate_cost_impact: item.claim_raised_by_estimate_cost_impact,
//         claim_raised_to_estimate_cost_impact: item.claim_raised_to_estimate_cost_impact,
//         approved_cost_impact: item.approved_cost_impact,
//         claim_raised_by_estimate_time_impact: item.claim_raised_by_estimate_time_impact,
//         claim_raised_to_estimate_time_impact: item.claim_raised_to_estimate_time_impact,
//         approved_time_impact: item.approved_time_impact,
//         actual_date_of_closure: formatDate(item.actual_date_of_closure),
//     }));

// // Flatten formatter (no children)
// const formatOtherData = (data) =>
//     data.map((item) => ({
//         change_number: item.change_number,
//         package: item.package,
//         sub_package1: item.sub_package1,
//         approved_status: item.approved_status,
//         status: item.status,
//         createddate: formatDate(item.createddate),
//         contractor_number: item.contractor_number,
//         consultant_contractor: item.consultant_contractor,
//         change_raised_to: item.change_raised_to,
//         change_received_on: formatDate(item.change_received_on),
//         change_raised_by_refrence: item.change_raised_by_refrence,
//         change_raised_by_date_received: formatDate(item.change_raised_by_date_received),
//         change_raised_to_date_of_response: formatDate(item.change_raised_to_date_of_response),
//         change_raised_to_position: item.change_raised_to_position,
//         approved_cost_impact: item.approved_cost_impact,
//         change_raised_by_estimate_cost_impact: item.change_raised_by_estimate_cost_impact,
//         change_raised_to_estimate_cost_impact: item.change_raised_to_estimate_cost_impact,
//         change_raised_by_estimate_time_impact: item.change_raised_by_estimate_time_impact,
//         change_raised_to_estimate_time_impact: item.change_raised_to_estimate_time_impact,
//         approved_time_impact: item.approved_time_impact,
//         actual_date_of_closure: formatDate(item.actual_date_of_closure),
//     }));

export default function MilestonesAndRisksTable() {

    const newtheme = useTheme();
    const { projectId } = useParams();
    const [tabIndex, setTabIndex] = useState(0);
    const currentTab = tabIndex === 0 ? "milestones" : "risks";

    // const {
    //     data: allClaims,
    //     isLoading: isLoadingClaims,
    //     error: errorClaims,
    // } = useGetAllClaimsQuery({
    //     ProjectId: projectId,
    //     sortBy: "createddate",
    //     sortOrder: "desc",
    //     approved_status: "Approved",
    // });

    // const {
    //     data: allChange,
    //     isLoading: isLoadingChangeOrders,
    //     error: errorChange,
    // } = useGetAllChangeQuery({
    //     ProjectId: projectId,
    //     sortBy: "createddate",
    //     sortOrder: "desc",
    //     approved_status: "Approved",
    // });

    // const dataPerson = allClaims?.data ? formatScheduleData(allClaims?.data) : [];

    // const dataOther = allChange?.data ? formatOtherData(allChange?.data) : [];

    const finalData = currentTab === "milestones" ? milestoneRows : risksRows;

    const columns = currentTab === "milestones" ? columnsMilestones || [] : columnsRisks || [];

    // const isLoading = isLoadingClaims || isLoadingChangeOrders;
    // const error = Boolean(errorClaims) || Boolean(errorChange);

    const otherColumnIds = columnsRisks.map(c =>
        typeof c.accessorKey === 'string' ? c.accessorKey : ''
    );
    const [otherColumnOrder, setOtherColumnOrder] = useState(otherColumnIds);

    return (
        <Box sx={{
            width: "100%", border: "1px solid #D8D8D8", mt: "10px", borderRadius: "12px", overflow: "hidden",
        }}>
            <Box sx={{ backgroundColor: "#fff", p: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 500, color: `${newtheme?.customSettings?.themeColor}`, fontSize: "18px" }}>
                    List of {currentTab === 'risks' ? "High-Impact Risks" : "Key Milestones Delayed"}
                </Typography>
                {/* <Typography sx={{ mb: 1, color: "#666" }}>
                    Get a hollistic view of how work is being priotised
                </Typography> */}

                <Tabs
                    value={tabIndex}
                    onChange={(_, newVal) => setTabIndex(newVal)}
                    sx={{
                        minHeight: 0,
                        paddingTop: "13px",
                        "& .MuiTabs-flexContainer": {
                            gap: "24px",
                        },
                        "& .MuiTabs-indicator": {
                            display: "flex",
                            justifyContent: "center",
                            backgroundColor: "transparent",
                            height: "2px",
                        },
                        "& .MuiTabs-indicator::after": {
                            content: '""',
                            display: "block",
                            height: "2px",
                            backgroundColor: newtheme?.customSettings?.themeColor || "#000",
                            width: "100%",
                            maxWidth: "100%",
                        },
                    }}
                >
                    <Tab
                        disableRipple
                        label="Milestones"
                        sx={{
                            minHeight: "0 !important",
                            padding: "0 !important",
                            paddingBottom: "10px !important",
                            alignItems: "flex-start",
                            textTransform: "none",
                            fontSize: "14px",
                            color: "#696969",
                            minWidth: "auto",
                            "&.Mui-selected": {
                                color: newtheme?.customSettings?.themeColor || "#696969",
                                fontWeight: 500,
                            },
                        }}
                    />
                    <Tab
                        disableRipple
                        label="Risks"
                        sx={{
                            minHeight: "0 !important",
                            padding: "0 !important",
                            paddingBottom: "10px !important",
                            alignItems: "flex-start",
                            textTransform: "none",
                            fontSize: "14px",
                            color: "#696969",
                            minWidth: "auto",
                            "&.Mui-selected": {
                                color: newtheme?.customSettings?.themeColor || "#696969",
                                fontWeight: 500,
                            },
                        }}
                    />
                </Tabs>

            </Box>
            {/* {isLoading ? (
                <Box display="flex" justifyContent="center" mt={4} pb={1}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Typography color="error">Failed to load data.</Typography>
            ) : ( */}
            <MaterialReactTable
                columns={columns}
                data={finalData}
                state={currentTab === 'risks' ? { columnOrder: otherColumnOrder } : {}}
                onColumnOrderChange={newOrder => {
                    if (currentTab === 'risks') setOtherColumnOrder(newOrder);
                }}
                muiTableHeadCellProps={{
                    sx: {
                        fontWeight: 500,
                        fontSize: "14px",
                        color: "#505258",
                        padding: "10px 14px",
                        backgroundColor: "#F8F8F8",
                        border: "1px solid #D8D8D8",
                    },
                }}
                muiTableBodyRowProps={{
                    sx: {
                        fontWeight: 400,
                        fontSize: "14px",
                        color: "#505258",
                        padding: "10px 14px",
                        backgroundColor: "#FFFFFF",
                        border: "1px solid #D8D8D8",
                        opacity: "0.54"
                    },
                }}
                enableExpanding={false}
                enableRowSelection={false}
                enableFullScreenToggle={false}
                //enableColumnOrdering={false}
                enableColumnActions={false}
            />
            {/* )} */}
        </Box>
    );
}